import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, endpoints } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";

export const CSRForm = () => {
  const [form, setForm] = useState({ company_name: "", contact_person: "", email: "", phone: "", budget_range: "₹5L–₹25L", focus_area: "Education", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(endpoints.csr, form);
      setDone(true);
      toast.success("Thank you — our partnerships team will reach out shortly.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Please try again."); }
    finally { setLoading(false); }
  };

  if (done) return (
    <div data-testid="csr-form-success" className="rounded-xl border border-[hsl(var(--green)/0.25)] bg-[hsl(var(--surface-tint-green))] p-6"><CheckCircle2 className="h-8 w-8 text-[hsl(var(--green))]" /><h3 className="heading-3 mt-2">Inquiry received</h3><p className="lead mt-1">Our partnerships team will contact you within 2 business days with a proposal.</p></div>
  );

  return (
    <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2" data-testid="csr-form">
      <Field label="Company name*"><Input required data-testid="csr-form-company_name" value={form.company_name} onChange={set("company_name")} /></Field>
      <Field label="Contact person*"><Input required data-testid="csr-form-contact_person" value={form.contact_person} onChange={set("contact_person")} /></Field>
      <Field label="Email*"><Input required type="email" data-testid="csr-form-email" value={form.email} onChange={set("email")} /></Field>
      <Field label="Phone*"><Input required data-testid="csr-form-phone" value={form.phone} onChange={set("phone")} /></Field>
      <Field label="Approximate budget">
        <Select value={form.budget_range} onValueChange={set("budget_range")}>
          <SelectTrigger data-testid="csr-form-budget_range"><SelectValue /></SelectTrigger>
          <SelectContent>{["< ₹5L","₹5L–₹25L","₹25L–₹1Cr","₹1Cr–₹5Cr","> ₹5Cr"].map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
        </Select>
      </Field>
      <Field label="Focus area">
        <Select value={form.focus_area} onValueChange={set("focus_area")}>
          <SelectTrigger data-testid="csr-form-focus_area"><SelectValue /></SelectTrigger>
          <SelectContent>{["Education","Health","Environment","Skilling","Disaster Relief","Women's Empowerment"].map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
        </Select>
      </Field>
      <div className="md:col-span-2"><Field label="Message"><Textarea rows={4} data-testid="csr-form-message" value={form.message} onChange={set("message")} /></Field></div>
      <div className="md:col-span-2 flex justify-end"><Button disabled={loading} type="submit" data-testid="csr-form-submit" className="bg-[hsl(var(--navy))] hover:bg-[hsl(var(--navy-2))] text-background">{loading ? "Submitting..." : "Request proposal"}</Button></div>
    </form>
  );
};

const Field = ({ label, children }) => (
  <div><Label className="text-sm font-medium text-[hsl(var(--navy))]">{label}</Label><div className="mt-1.5">{children}</div></div>
);
